import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { StampPromo } from "../components/StampPromo";
import { getCompany } from "../lib/api";
import { formatSiren, formatSiret, formatDate, formatDateTime, formatCapital, titleCase } from "../lib/format";
import { useHead } from "../lib/useHead";
import { addToHistory } from "../lib/history";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Check, MapPin, Users, Building, Landmark,
  AlertTriangle, Loader2, Info, Database, Hash, Share2, Printer, ExternalLink,
} from "lucide-react";

const NA = () => <span className="font-normal text-[hsl(var(--fg-muted))]">Non disponible</span>;

const Row = ({ label, children, testId }) => (
  <div className="flex flex-col gap-0.5 border-b border-border py-3 last:border-0 sm:flex-row sm:items-baseline sm:gap-6">
    <dt className="w-full shrink-0 text-[13px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--fg-secondary))] sm:w-52">{label}</dt>
    <dd data-testid={testId} className="text-base font-medium text-[hsl(var(--fg-primary))]">{children}</dd>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <section data-testid={testId} className="rounded-lg border border-border bg-[hsl(var(--surface))]">
    <div className="flex items-center gap-2 border-b border-border px-5 py-3">
      <Icon className="h-4 w-4 text-[hsl(var(--brand))]" />
      <h2 className="font-display text-base font-semibold uppercase tracking-wide text-[hsl(var(--fg-primary))]">{title}</h2>
    </div>
    <div className="px-5 py-2">{children}</div>
  </section>
);

export default function Company() {
  const { siren } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setState({ loading: true, error: null, data: null });
      try {
        const data = await getCompany(siren);
        if (active) setState({ loading: false, error: null, data });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (active) setState({ loading: false, error: { status, message: detail || "Impossible de charger la fiche." }, data: null });
      }
    })();
    return () => { active = false; };
  }, [siren]);

  const c0 = state.data;
  const seoNom = c0 ? titleCase(c0.nom_complet || c0.nom_raison_sociale || "Entreprise") : null;
  useHead({
    title: seoNom ? `${seoNom} — SIREN ${formatSiren(siren)} | InfoSociété` : `Entreprise ${formatSiren(siren)} — InfoSociété`,
    description: c0
      ? `${seoNom} · SIREN ${formatSiren(c0.siren)}${c0.forme_juridique?.libelle ? ` · ${c0.forme_juridique.libelle}` : ""}${c0.siege?.ville ? ` · ${titleCase(c0.siege.ville)}` : ""}. Informations publiques (INSEE/RNE) sur InfoSociété.`
      : "Fiche d'entreprise française — informations publiques sur InfoSociété.",
    robots: c0 && c0.etat_administratif ? "index,follow" : "noindex,follow",
  });

  useEffect(() => {
    if (c0?.siren) {
      addToHistory({
        siren: c0.siren,
        nom: titleCase(c0.nom_complet || c0.nom_raison_sociale || "Entreprise"),
        ville: c0.siege?.ville ? titleCase(c0.siege.ville) : null,
        etat: c0.etat_administratif,
      });
    }
  }, [c0]);

  const sharePage = async () => {
    const url = window.location.href;
    const title = seoNom || "Fiche entreprise";
    if (navigator.share) {
      try { await navigator.share({ title: `${title} — InfoSociété`, url }); return; } catch { /* annulé */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié", { description: "Partagez cette fiche avec un collègue." });
    } catch {
      toast.error("Impossible de copier le lien automatiquement.");
    }
  };

  const downloadPdf = () => window.print();

  const copyCoords = async () => {
    const texte = state.data?.coordonnees?.texte;
    if (!texte) return;
    const done = () => {
      setCopied(true);
      toast.success("Coordonnées copiées", { description: "Prêtes à coller sur une facture ou un tampon." });
      setTimeout(() => setCopied(false), 2200);
    };
    try {
      await navigator.clipboard.writeText(texte);
      done();
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = texte;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) done();
        else toast.error("Impossible de copier automatiquement. Sélectionnez le texte manuellement.");
      } catch {
        toast.error("Impossible de copier automatiquement. Sélectionnez le texte manuellement.");
      }
    }
  };

  if (state.loading) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-4xl items-center justify-center gap-2 px-5 py-28 text-[hsl(var(--fg-muted))]">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement de la fiche…
        </div>
      </Layout>
    );
  }

  if (state.error) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-5 py-20">
          <div data-testid="company-error" className="rounded-lg border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/5 p-8 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-[hsl(var(--warn))]" />
            <h1 className="mt-4 font-display text-xl font-semibold text-[hsl(var(--fg-primary))]">
              {state.error.status === 404 ? "Entreprise introuvable" : "Fiche indisponible"}
            </h1>
            <p className="mt-2 text-sm text-[hsl(var(--fg-secondary))]">{state.error.message}</p>
            <Link to="/" className="mt-5 inline-block text-sm font-medium text-[hsl(var(--brand))] hover:underline">
              Retour à la recherche
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const c = state.data;
  const nom = titleCase(c.nom_complet || c.nom_raison_sociale || "Entreprise");
  const ville = c.siege?.ville ? titleCase(c.siege.ville) : null;

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-5 py-8">
        <Link to="/" data-testid="back-link" className="no-print inline-flex items-center gap-1.5 text-sm text-[hsl(var(--fg-secondary))] transition-colors hover:text-[hsl(var(--brand))]">
          <ArrowLeft className="h-4 w-4" /> Nouvelle recherche
        </Link>

        {/* En-tête imprimé (PDF) */}
        <div className="print-only mb-4">
          <p className="font-display text-lg font-semibold">InfoSociété — fiche d'entreprise</p>
          <p className="text-sm">Source : {c.source?.nom} · Récupéré le {formatDateTime(c.source?.date_recuperation)}</p>
        </div>

        {/* En-tête */}
        <header className="mt-4 rounded-lg border border-border bg-[hsl(var(--surface))] p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 data-testid="company-name" className="font-display text-2xl font-semibold leading-tight tracking-tight text-[hsl(var(--fg-primary))] sm:text-3xl">
                {nom}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-base text-[hsl(var(--fg-secondary))]">
                <span className="font-mono">SIREN {c.siren_formate || formatSiren(c.siren)}</span>
                {c.forme_juridique?.libelle && <span>{c.forme_juridique.libelle}</span>}
                {ville && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{ville}</span>}
              </div>
            </div>
            <StatusBadge etat={c.etat_administratif} libelle={c.etat_libelle} size="lg" testId="company-status" />
          </div>

          <div className="no-print mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
            <button
              data-testid="share-btn"
              onClick={sharePage}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2 text-sm font-medium text-[hsl(var(--fg-primary))] transition-colors hover:bg-muted"
            >
              <Share2 className="h-4 w-4" /> Partager la fiche
            </button>
            <button
              data-testid="pdf-btn"
              onClick={downloadPdf}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2 text-sm font-medium text-[hsl(var(--fg-primary))] transition-colors hover:bg-muted"
            >
              <Printer className="h-4 w-4" /> Télécharger en PDF
            </button>
          </div>
        </header>

        <div className="print-full mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Colonne principale */}
          <div className="space-y-6 lg:col-span-2">
            <SectionCard title="Identité" icon={Building} testId="section-identite">
              <dl>
                <Row label="Raison sociale" testId="row-raison-sociale">{titleCase(c.nom_raison_sociale) || <NA />}</Row>
                <Row label="Nom commercial" testId="row-nom-commercial">{c.nom_commercial ? titleCase(c.nom_commercial) : <NA />}</Row>
                <Row label="Sigle" testId="row-sigle">{c.sigle || <NA />}</Row>
                <Row label="Forme juridique" testId="row-forme">
                  {c.forme_juridique?.libelle ? (
                    <>{c.forme_juridique.libelle}{c.forme_juridique.code ? <span className="ml-1 text-sm font-normal text-[hsl(var(--fg-muted))]">(code {c.forme_juridique.code})</span> : null}</>
                  ) : <NA />}
                </Row>
                <Row label="Date de création" testId="row-creation">{formatDate(c.date_creation) || <NA />}</Row>
                <Row label="Statut administratif" testId="row-statut">
                  <StatusBadge etat={c.etat_administratif} libelle={c.etat_libelle} />
                </Row>
              </dl>
            </SectionCard>

            <SectionCard title="Immatriculation & activité" icon={Landmark} testId="section-immatriculation">
              <dl>
                <Row label="SIREN" testId="row-siren">{c.siren_formate || formatSiren(c.siren)}</Row>
                <Row label="SIRET du siège" testId="row-siret">{c.siege?.siret ? formatSiret(c.siege.siret) : <NA />}</Row>
                <Row label="Mention RCS" testId="row-rcs">
                  {c.rcs?.numero ? (
                    <div>
                      <span className="font-mono" data-testid="rcs-mention">{c.rcs.mention || `RCS ${formatSiren(c.rcs.numero)}`}</span>
                      <p className="mt-1 text-sm font-normal text-[hsl(var(--fg-muted))]">{c.rcs.note}</p>
                    </div>
                  ) : <NA />}
                </Row>
                <Row label="Activité principale" testId="row-activite">
                  {c.activite?.libelle || c.activite?.code ? (
                    <div>
                      <span>{c.activite.libelle || "Activité"}</span>
                      {c.activite.code && <span className="ml-1 font-mono text-sm font-normal text-[hsl(var(--fg-muted))]">(APE/NAF {c.activite.code})</span>}
                      {c.activite.section_libelle && <p className="mt-1 text-sm font-normal text-[hsl(var(--fg-muted))]">Section : {c.activite.section_libelle}</p>}
                    </div>
                  ) : <NA />}
                </Row>
                <Row label="Ville du greffe" testId="row-greffe">
                  {c.rcs?.greffe ? (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span data-testid="greffe-ville">{c.rcs.greffe}</span>
                      {c.rcs.infogreffe_url && (
                        <a
                          data-testid="infogreffe-link"
                          href={c.rcs.infogreffe_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--brand))] underline-offset-2 transition-colors hover:underline"
                        >
                          Fiche Infogreffe <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ) : <NA />}
                </Row>
                <Row label="Capital social" testId="row-capital">
                  {c.capital_social?.disponible ? (
                    <div>
                      <span data-testid="capital-montant">
                        {formatCapital(c.capital_social.montant, c.capital_social.devise)}
                        {c.capital_social.variable && <span className="ml-1 text-sm font-normal text-[hsl(var(--fg-muted))]">(capital variable)</span>}
                      </span>
                      <p className="mt-1 text-sm font-normal text-[hsl(var(--fg-muted))]">Source : {c.capital_social.source}</p>
                    </div>
                  ) : (
                    <div>
                      <NA />
                      <p className="mt-1 text-sm font-normal text-[hsl(var(--fg-muted))]">{c.capital_social?.note || "Non fourni par la source utilisée."}</p>
                    </div>
                  )}
                </Row>
                <Row label="N° de TVA" testId="row-tva">
                  {c.tva?.numero ? (
                    <div>
                      <span className="font-mono">{c.tva.numero}</span>
                      <p className="mt-1 inline-flex items-center gap-1 text-sm font-normal text-[hsl(var(--warn))]">
                        <Info className="h-3 w-3" /> {c.tva.note}
                      </p>
                    </div>
                  ) : <NA />}
                </Row>
              </dl>
            </SectionCard>

            <SectionCard title="Siège social" icon={MapPin} testId="section-siege">
              <dl>
                <Row label="Adresse" testId="row-adresse">{c.siege?.adresse ? titleCase(c.siege.adresse) : <NA />}</Row>
                <Row label="Établissement" testId="row-etab-siege">
                  {c.siege?.siret ? (
                    <span>Siège · SIRET {formatSiret(c.siege.siret)} · <StatusBadge etat={c.siege.etat_administratif} libelle={c.siege.etat_libelle} /></span>
                  ) : <NA />}
                </Row>
                <Row label="Établissements" testId="row-nb-etab">
                  {c.nombre_etablissements != null ? (
                    <span>{c.nombre_etablissements} au total{c.nombre_etablissements_ouverts != null ? ` · ${c.nombre_etablissements_ouverts} ouvert(s)` : ""}</span>
                  ) : <NA />}
                </Row>
              </dl>
            </SectionCard>

            {c.dirigeants?.length > 0 && (
              <SectionCard title="Dirigeants" icon={Users} testId="section-dirigeants">
                <ul className="divide-y divide-border">
                  {c.dirigeants.map((d, i) => (
                    <li key={i} data-testid={`dirigeant-${i}`} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-base font-medium text-[hsl(var(--fg-primary))]">{d.nom ? titleCase(d.nom) : "Non précisé"}</p>
                        <p className="text-sm text-[hsl(var(--fg-muted))]">{d.type}{d.annee_naissance ? ` · né(e) en ${d.annee_naissance}` : ""}</p>
                      </div>
                      {d.qualite && <span className="rounded-full border border-border bg-[hsl(var(--background))] px-2.5 py-0.5 text-sm text-[hsl(var(--fg-secondary))]">{d.qualite}</span>}
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}

            {c.etablissements?.length > 0 && (
              <SectionCard title="Établissements" icon={Building} testId="section-etablissements">
                <ul className="divide-y divide-border">
                  {c.etablissements.map((e, i) => (
                    <li key={e.siret || i} data-testid={`etablissement-${i}`} className="flex items-start justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-[hsl(var(--fg-muted))]">SIRET {formatSiret(e.siret)}{e.est_siege ? " · Siège" : ""}</p>
                        <p className="mt-0.5 truncate text-base text-[hsl(var(--fg-primary))]">{e.adresse ? titleCase(e.adresse) : (e.ville ? titleCase(e.ville) : "Adresse non disponible")}</p>
                      </div>
                      {e.etat_administratif && <StatusBadge etat={e.etat_administratif} libelle={e.etat_libelle} />}
                    </li>
                  ))}
                </ul>
                <p className="py-2 text-sm text-[hsl(var(--fg-muted))]">Jusqu'à 10 établissements affichés par la source.</p>
              </SectionCard>
            )}
          </div>

          {/* Colonne latérale : actions + conversion */}
          <aside className="space-y-6">
            <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5">
              <h2 className="flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-[hsl(var(--fg-primary))]">
                <Hash className="h-4 w-4 text-[hsl(var(--brand))]" /> Coordonnées
              </h2>
              <pre data-testid="coordonnees-text" className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-[hsl(var(--background))] p-3 font-mono text-sm leading-relaxed text-[hsl(var(--fg-primary))]">
{c.coordonnees?.texte || "Non disponible"}
              </pre>
              <button
                data-testid="copy-coords-btn"
                onClick={copyCoords}
                className="no-print mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2.5 text-sm font-medium text-[hsl(var(--fg-primary))] transition-colors hover:bg-muted"
              >
                {copied ? <><Check className="h-4 w-4 text-[hsl(var(--ok))]" /> Copié</> : <><Copy className="h-4 w-4" /> Copier les coordonnées</>}
              </button>
            </div>

            <div className="no-print">
              <StampPromo coordonnees={c.coordonnees} siren={c.siren} />
            </div>

            <div data-testid="source-block" className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5">
              <h2 className="flex items-center gap-2 font-display text-base font-semibold uppercase tracking-wide text-[hsl(var(--fg-primary))]">
                <Database className="h-4 w-4 text-[hsl(var(--brand))]" /> Source
              </h2>
              <dl className="mt-3 space-y-2 text-sm text-[hsl(var(--fg-secondary))]">
                <div><dt className="text-[hsl(var(--fg-muted))]">Origine</dt><dd>{c.source?.nom}</dd></div>
                <div><dt className="text-[hsl(var(--fg-muted))]">Récupéré le</dt><dd data-testid="source-date">{formatDateTime(c.source?.date_recuperation)}</dd></div>
                {c.source?.date_mise_a_jour_source && (
                  <div><dt className="text-[hsl(var(--fg-muted))]">Mise à jour source</dt><dd>{formatDateTime(c.source.date_mise_a_jour_source)}</dd></div>
                )}
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
